/**
 * BackgroundJobsContext - Global context for tracking background jobs across the app.
 * 
 * This context persists job tracking even when users navigate away from BankFeedCenterPage.
 * Jobs are stored in localStorage and state, with WebSocket listeners for real-time updates.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

import { BACKEND_BASE } from '../utils/apiUrl';
import { notifyUploadCancelled } from '../utils/uploadCancelToast';
// iter1238 Phase 8.9 — `notifyActionNeeded` intentionally NOT imported.
// The "Action needed" toast was retired; users control pending-backlog
// jobs via the pill on the Bank Account page (MyBanksPage.jsx).
const API = BACKEND_BASE;

// Storage key for persisting jobs across page refreshes
const STORAGE_KEY = 'akountfly_background_jobs';

// Time threshold (in ms) before showing "processing in background" message
const BACKGROUND_THRESHOLD_MS = 30000; // 30 seconds

const BackgroundJobsContext = createContext(null);

export const useBackgroundJobs = () => {
  const context = useContext(BackgroundJobsContext);
  if (!context) {
    throw new Error('useBackgroundJobs must be used within a BackgroundJobsProvider');
  }
  return context;
};

export const BackgroundJobsProvider = ({ children }) => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const pollIntervalsRef = useRef(new Map());
  const isMountedRef = useRef(true);
  // iter1241 · toast-dedup guard. Terminal-state toasts (failed,
  // rejected, cancelled, blocked_continuity) fire from inside the
  // polling loop; without this set, two consecutive poll ticks could
  // fire the SAME toast twice before the 10s cleanup timeout kicks
  // in. One toast per job-lifetime, guaranteed.
  const toastFiredRef = useRef(new Set());
  const markToastFired = useCallback((jobId, kind) => {
    const key = `${jobId}::${kind}`;
    if (toastFiredRef.current.has(key)) return false;
    toastFiredRef.current.add(key);
    return true;
  }, []);

  // Load jobs from localStorage on mount
  useEffect(() => {
    isMountedRef.current = true;
    
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsedJobs = JSON.parse(stored);
        const now = Date.now();
        const hourAgo = new Date(now - 60 * 60 * 1000);
        const dayAgo = new Date(now - 24 * 60 * 60 * 1000);
        // Filter:
        //  - completed/failed jobs older than 1 hour → drop
        //  - in-flight ("processing"/"pending"/no status) jobs older than
        //    24 hours → drop. Without this filter, zombie jobs whose
        //    backend records have already been GC'd kept being re-polled
        //    on every page load, hit the 5-minute timeout, and fired
        //    the "Background job is taking longer than expected" toast
        //    randomly across the app - that was the user-reported
        //    "notification randomly showing on the platform" issue.
        const activeJobs = parsedJobs.filter(job => {
          if (
            job.status === 'completed' || job.status === 'failed' ||
            job.status === 'cancelled' || job.status === 'rejected' ||
            job.status === 'blocked_continuity' || job.status === 'timeout'
          ) {
            // 'timeout' is a legacy status from iter1236 and earlier. We
            // still recognise it here so any localStorage carrying that
            // ghost status expires it after an hour instead of polling
            // forever, but we no longer produce new 'timeout' jobs.
            const completedTime = new Date(job.completedAt || job.startedAt);
            return completedTime > hourAgo;
          }
          // In-flight job - keep only if started within last 24 hours.
          const startedTime = new Date(job.startedAt || 0);
          return startedTime > dayAgo;
        });
        setJobs(activeJobs);
      }
    } catch (e) {
      console.error('Failed to load background jobs from storage:', e);
    }
    
    setIsInitialized(true);
    
    return () => {
      isMountedRef.current = false;
      // Clean up all polling timers (setTimeout handles now, not setInterval).
      pollIntervalsRef.current.forEach(handle => clearTimeout(handle));
      pollIntervalsRef.current.clear();
    };
  }, []);

  // iter1230 - Server-side hydration. localStorage-only tracking meant a
  // fresh browser session, a hard refresh, or a device switch dropped
  // in-flight uploads on the floor (no progress card, no completion
  // toast). Merge any active jobs the backend still owns for this user.
  useEffect(() => {
    if (!isInitialized) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/bank-feeds/upload-jobs/active`);
        const serverJobs = (res.data?.jobs || []);
        if (cancelled || !isMountedRef.current || serverJobs.length === 0) return;
        setJobs(prev => {
          const known = new Set(prev.map(j => j.jobId));
          const merged = [...prev];
          for (const sj of serverJobs) {
            if (known.has(sj.job_id)) continue;
            merged.push({
              jobId: sj.job_id,
              filename: sj.filename,
              startedAt: sj.created_at || new Date().toISOString(),
              status: sj.status || 'processing',
              progress: sj.progress_percent || 0,
              totalTx: sj.total_transactions || 0,
              processedTx: sj.processed_transactions || 0,
              highConfidence: 0,
              reviewNeeded: 0,
              statusMessage: sj.status_message,
              billing: sj.billing || null,
              bridgePreview: sj.bridge_preview || null,
              notifiedBackground: false,
              hydratedFromServer: true,
            });
          }
          return merged;
        });
      } catch (_) {
        /* non-fatal – tracker just relies on localStorage this session */
      }
    })();
    return () => { cancelled = true; };
  }, [isInitialized]);

  // Save jobs to localStorage whenever they change
  useEffect(() => {
    if (isInitialized) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(jobs));
      } catch (e) {
        console.error('Failed to save background jobs to storage:', e);
      }
    }
  }, [jobs, isInitialized]);

  // iter1237 - Adaptive polling with NO hard timeout.
  // -------------------------------------------------------------------
  // The prior implementation used setInterval(2s) with MAX_POLLS=150
  // (5 minute cap). For any statement that took longer than 5 minutes
  // to parse - which happens on all AI-fallback PDFs (opeyemi's Henatos
  // file needs ~15 min for the four-chunk gpt-5.2 pass) - the frontend
  // would flip the job to a local `timeout` status and paint the red
  // "Upload took too long / Review failure" banner even though the
  // backend was still parsing successfully.
  //
  // New model: adaptive backoff via setTimeout, bounded ONLY by the
  // backend reaching a terminal status. We NEVER invent a client-side
  // `timeout` state again. Backoff schedule (elapsed since job start):
  //     0 -  30s  → 2s
  //    30 - 120s  → 5s
  //   120 - 300s  → 10s
  //   300 - 900s  → 30s
  //   >=  900s   → 60s
  // When the tab is hidden we clamp to 60s minimum. On tab visibility
  // return we poll immediately once so the user sees fresh state.
  const startPolling = useCallback((jobId, startedAtOverride) => {
    if (pollIntervalsRef.current.has(jobId)) return;

    const startedMs = startedAtOverride
      ? Date.parse(startedAtOverride)
      : Date.now();

    const nextDelayMs = () => {
      const elapsed = Date.now() - startedMs;
      const hidden = typeof document !== 'undefined' && document.hidden;
      let base;
      if (elapsed < 30_000) base = 2_000;
      else if (elapsed < 120_000) base = 5_000;
      else if (elapsed < 300_000) base = 10_000;
      else if (elapsed < 900_000) base = 30_000;
      else base = 60_000;
      return hidden ? Math.max(base, 60_000) : base;
    };

    const stop = () => {
      const handle = pollIntervalsRef.current.get(jobId);
      if (handle) {
        clearTimeout(handle);
        pollIntervalsRef.current.delete(jobId);
      }
    };

    const schedule = (delay) => {
      const handle = setTimeout(runOnce, delay);
      pollIntervalsRef.current.set(jobId, handle);
    };

    const runOnce = async () => {
      if (!isMountedRef.current) { stop(); return; }
      let terminal = false;
      try {
        const response = await axios.get(`${API}/api/bank-feeds/upload-job-status/${jobId}`);
        const status = response.data;

        setJobs(prev => prev.map(job => {
          if (job.jobId !== jobId) return job;

          const updated = {
            ...job,
            status: status.status,
            progress: status.progress_percent || 0,
            totalTx: status.total_transactions || 0,
            processedTx: status.processed_transactions || 0,
            // iter1245 · Monotonic shard-based counters powering the
            // "Posted X of Y" banner. These never regress across
            // watchdog resumes, so the user sees smooth progress.
            feedPostedCount: status.feed_posted_count || 0,
            totalCanonicalTxs: status.total_canonical_txs || 0,
            progressStage: status.progress_stage || job.progressStage || null,
            highConfidence: status.high_confidence || 0,
            reviewNeeded: status.review_needed || 0,
            // Prefer the backend's live status_message so the banner
            // narrates real progress ("Classifying 450/1228…") instead
            // of hard-coded phrases.
            statusMessage: status.status_message || job.statusMessage,
            // iter1238 Phase 8.11 · surface the detailed error_message
            // (distinct from status_message) so the failure modal can
            // show both — status_message is the user-facing summary,
            // error_message is the technical detail.
            errorMessage: status.error_message || job.errorMessage,
            // iter1241 · continuity guard payload for the banner /
            // toast to render case-specific copy on `blocked_continuity`.
            continuityReport: status.continuity_report || job.continuityReport || null,
            batch: status.batch || job.batch || null,
            learnedLayout: status.learned_layout || job.learnedLayout || false,
            cancelReason: status.cancel_reason || job.cancelReason || null,
            // iter1238 Phase 8.10 · P1.3 — successful poll clears the
            // 404-debounce counter (see catch block below).
            _404Count: 0,
            _404FirstAt: null,
          };

          if (status.status === 'completed') {
            terminal = true;
            updated.completedAt = new Date().toISOString();
            showCompletionNotification(updated);
            if (status.integrity_warning && markToastFired(jobId, 'integrity_warning')) {
              toast.warning(
                <div data-testid="upload-integrity-warning-toast">
                  <strong>&quot;{job.filename}&quot; imported with a balance warning</strong>
                  <div className="text-sm mt-1">{status.integrity_warning}</div>
                </div>,
                { duration: 20000 }
              );
            }
            // iter1443 R5 — cross-page refresh signal so an open Bank
            // Feeds page (or one the user opens right after this poll
            // fires) picks up the fresh transactions without the user
            // having to hit the browser Refresh button. See the
            // matching `akf:bank-feed-refresh` listener in
            // BankFeedCenterPage.
            try {
              window.dispatchEvent(new CustomEvent('akf:bank-feed-refresh'));
            } catch (_e) { /* SSR / test envs */ }
            // Auto-dismiss the green success banner after a short flash.
            setTimeout(() => {
              if (!isMountedRef.current) return;
              setJobs((p) => p.filter((j) => j.jobId !== jobId));
            }, 8000);
          } else if (status.status === 'failed') {
            terminal = true;
            updated.completedAt = new Date().toISOString();
            updated.error = status.error || 'Processing failed';
            if (markToastFired(jobId, 'failed')) {
              toast.error(
                <div>
                  <strong>Bank Feed Processing Failed</strong>
                  <div className="text-sm mt-1">
                    &quot;{job.filename}&quot; could not be processed.
                  </div>
                </div>,
                { duration: 10000 }
              );
            }
          } else if (status.status === 'cancelled') {
            terminal = true;
            updated.completedAt = new Date().toISOString();
            if (!job.cancelledNotified && markToastFired(jobId, 'cancelled')) {
              updated.cancelledNotified = true;
              notifyUploadCancelled(jobId, job.filename);
              setTimeout(() => {
                if (!isMountedRef.current) return;
                setJobs((p) => p.filter((j) => j.jobId !== jobId));
              }, 4000);
            }
          } else if (status.status === 'rejected') {
            // iter1238 Phase 8.13g P0.5 — dedicated toast surfacing the
            // backend's specific user-facing status_message (e.g. "This
            // PDF looks like a scanned image, please upload text-based
            // PDF or CSV/Excel"). Without this the banner just vanished
            // and the user saw nothing.
            terminal = true;
            updated.completedAt = new Date().toISOString();
            updated.error = status.status_message || status.error || null;
            const msg = status.status_message
              || status.error
              || "We could not read this PDF's layout. Please upload a text-based PDF, or a CSV/Excel export from your bank.";
            if (markToastFired(jobId, 'rejected')) {
              toast.error(
                <div>
                  <strong>Upload rejected</strong>
                  <div className="text-sm mt-1">&quot;{job.filename}&quot;</div>
                  <div className="text-sm mt-1">{msg}</div>
                </div>,
                { duration: 15000 }
              );
            }
            setTimeout(() => {
              if (!isMountedRef.current) return;
              setJobs((p) => p.filter((j) => j.jobId !== jobId));
            }, 6000);
          } else if (status.status === 'blocked_continuity') {
            // iter1241 · Continuity guard blocked the import. Surface
            // a case-specific toast so the user learns WHY without
            // having to open Bank Feeds and hunt for the message.
            // Covers: fully_covered, gap_detected, continuity_mismatch,
            // no_boundary_row, backlog_charge_declined.
            terminal = true;
            updated.completedAt = new Date().toISOString();
            const rep = status.continuity_report || {};
            const caseKey = rep.case || status.cancel_reason || 'blocked';
            const backendMsg = status.status_message || rep.reason || null;
            let title = 'Upload blocked';
            let body = backendMsg || 'This statement could not be imported.';
            let missingRowsBlock = null;
            let toastAction = undefined;
            if (caseKey === 'fully_covered') {
              title = 'Nothing new to import';
              body = backendMsg
                || `This statement (${rep.new_period_start || '?'} → ${rep.new_period_end || '?'}) is already in your books.`;
            } else if (caseKey === 'gap_detected') {
              title = 'Gap detected in your statements';
              const b = rep.books_balance, s = rep.statement_balance;
              body = backendMsg
                || `Books close on ${rep.last_close_date || '?'} at ${b}, but the new statement opens at ${s}. Please upload the missing statement covering the intervening period first.`;
            } else if (caseKey === 'continuity_mismatch') {
              title = 'Continuity mismatch';
              body = backendMsg
                || `On ${rep.last_close_date || '?'}, books show ${rep.books_balance}, new statement shows ${rep.statement_balance}. This usually means the two files are from different accounts, or one of them has been edited.`;
              const mr = rep.missing_rows;
              if (mr && mr.missing_count > 0) {
                const shown = (mr.missing_rows || []).slice(0, 5);
                missingRowsBlock = (
                  <div className="mt-2 text-xs" data-testid="toast-missing-rows">
                    <div className="font-medium">{mr.missing_count} row{mr.missing_count === 1 ? '' : 's'} on the statement {mr.missing_count === 1 ? 'is' : 'are'} not in your books:</div>
                    <ul className="mt-1 space-y-0.5">
                      {shown.map((r, i) => (
                        <li key={i} className="truncate">{r.date} · {r.description || '—'} · {r.direction === 'inflow' ? '+' : '−'}{Math.abs(Number(r.amount || 0)).toLocaleString()}</li>
                      ))}
                      {mr.missing_count > shown.length && <li>+{mr.missing_count - shown.length} more</li>}
                    </ul>
                    {mr.can_add
                      ? <div className="mt-1">They explain the difference exactly — you can add them in one click.</div>
                      : <div className="mt-1">{mr.cannot_add_reason}</div>}
                  </div>
                );
                toastAction = {
                  label: mr.can_add ? `Add ${mr.missing_count} missing row${mr.missing_count === 1 ? '' : 's'}` : 'Review rows',
                  onClick: () => window.dispatchEvent(new CustomEvent('akf-missing-rows-open', { detail: { jobId, filename: job.filename } })),
                };
              }
            } else if (caseKey === 'no_boundary_row') {
              title = 'Overlap without a matching row';
              body = backendMsg
                || `The new statement overlaps existing books through ${rep.last_close_date || '?'} but has no row on or before that date to verify continuity. Try re-exporting with a wider date range.`;
            } else if (caseKey === 'backlog_charge_declined') {
              title = 'Upload cancelled — payment declined';
              body = backendMsg
                || `We couldn't charge your card, so nothing was recorded. Please update your card in Billing and re-upload.`;
            } else if (caseKey === 'bridge_mismatch' || caseKey === 'bridge_forced') {
              // iter1242 · Bridge check for a Mono/Plaid gap-rescue.
              // Compose a targeted message that names WHICH anchor
              // failed (opening, closing, both, or "unverifiable").
              const d = rep.detail || {};
              const preOk = d.pre_bridge_ok;
              const postOk = d.post_bridge_ok;
              title = caseKey === 'bridge_forced'
                ? 'Bridge override applied'
                : 'Gap-rescue statement does not bridge cleanly';
              const parts = [];
              if (preOk === false) {
                parts.push(
                  `Opening mismatch on ${d.pre_gap_close_date || '?'}: books close at ` +
                  `${d.pre_gap_close_derived ?? '?'} but your statement opens at ` +
                  `${d.manual_opening ?? '?'}.`
                );
              } else if (preOk === null || preOk === undefined) {
                parts.push('The statement has no printed opening balance we can compare.');
              }
              if (postOk === false) {
                parts.push(
                  `Closing mismatch on ${d.post_gap_first_row_date || '?'}: to bridge cleanly the ` +
                  `statement should close at ${d.post_gap_open_derived ?? '?'}, but it closes at ` +
                  `${d.manual_closing ?? '?'}.`
                );
              } else if (postOk === null || postOk === undefined) {
                parts.push('The statement has no printed closing balance we can compare.');
              }
              if (d.arithmetic_ok === false) {
                parts.push('The running balance inside the statement also does not add up.');
              }
              body = parts.length ? parts.join(' ') : (backendMsg || body);
            }
            updated.error = body;
            if (markToastFired(jobId, 'blocked_continuity')) {
              toast.error(
                <div>
                  <strong>{title}</strong>
                  <div className="text-sm mt-1 truncate">&quot;{job.filename}&quot;</div>
                  <div className="text-sm mt-1">{body}</div>
                  {missingRowsBlock}
                </div>,
                { duration: missingRowsBlock ? 30000 : 18000, action: toastAction }
              );
            }
            setTimeout(() => {
              if (!isMountedRef.current) return;
              setJobs((p) => p.filter((j) => j.jobId !== jobId));
            }, 10000);
          } else if (status.status === 'awaiting_start_date_decision') {
            // Start-date rules v2 · the statement reaches earlier than the accounting start; the
            // GlobalStartDateDecisionProvider opens the decision modals and re-tracks the job afterwards.
            terminal = true;
            updated.startDateOffer = status.start_date_offer || null;
          } else if (status.status === 'awaiting_sub_account_decision') {
            // Oct 2026 · statement prints several accounts (OPay Wallet + OWealth Savings);
            // GlobalSubAccountDecisionProvider asks how to import them and re-tracks the job.
            terminal = true;
            updated.subAccountOffer = status.sub_account_offer || null;
          } else if (status.status === 'awaiting_confirmation' || status.status === 'pending_payment') {
            terminal = true;
            updated.billing = status.billing || null;
            // iter1242 · surface the bridge preview stamped by the
            // backlog gate so the modal can render "opens at ₦X
            // (matches Mono close) / closes at ₦Y (matches Mono
            // resume)" — or "we couldn't verify the printed balance"
            // — BEFORE the user confirms.
            updated.bridgePreview = status.bridge_preview || job.bridgePreview || null;
            // iter1238 Phase 8.8 — stale preview_stage jobs from before the
            // pre-parse gate was removed carry wrong estimates. Silently
            // auto-cancel them so the "Action needed" toast + the
            // "Upload needs to be restarted" modal never surface. Users
            // can then re-upload cleanly.
            if (status.billing && status.billing.preview_stage) {
              (async () => {
                try {
                  await axios.post(`${API}/api/bank-feeds/upload-jobs/${jobId}/cancel`);
                } catch (_e) {
                  // swallow — the reaper on the backend will get it eventually
                }
                if (!isMountedRef.current) return;
                setJobs((p) => p.filter((j) => j.jobId !== jobId));
              })();
              return updated;
            }
            // iter1238 Phase 8.9 — "Action needed" toast REMOVED. Users
            // control the pending-backlog flow from the Bank Account
            // page pill ("Confirm / Cancel backlog"), not via a page-
            // load nag. Just mark notified so we don't retry.
            if (!job.awaitingNotified) {
              updated.awaitingNotified = true;
            }
          }
          return updated;
        }));
      } catch (error) {
        // iter1238 Phase 8.10 · P1.3 — 404 DEBOUNCE.
        // Previously a single 404 flipped local status → 'failed' and
        // fired the "Bank Feed Processing Failed" toast. This was the
        // frontend-side root cause of the Feb-17 opeyemi incident:
        // one transient 404 during a 20-min JWT-refresh window
        // permanently terminalised a job that was actually running
        // fine on the server. Now we require THREE consecutive 404s
        // (spanning at least 60s) before flipping. Transient network
        // errors keep polling.
        const isNotFound = error?.response?.status === 404;
        if (isNotFound) {
          const now = Date.now();
          setJobs(prev => prev.map(job => {
            if (job.jobId !== jobId) return job;
            const firstAt = job._404FirstAt || now;
            const count = (job._404Count || 0) + 1;
            const spanMs = now - firstAt;
            const shouldTerminate = count >= 3 && spanMs >= 60_000;
            if (shouldTerminate) {
              terminal = true;
              return {
                ...job,
                status: 'failed',
                completedAt: new Date().toISOString(),
                error: `Job record could not be located after ${count} attempts over ${Math.round(spanMs/1000)}s`,
              };
            }
            console.warn(
              `Poll ${jobId} got 404 (${count}/3 over ${Math.round(spanMs/1000)}s) — will retry`
            );
            return { ...job, _404FirstAt: firstAt, _404Count: count };
          }));
        } else {
          // Reset 404 counter on ANY non-404 response — including transient
          // 5xx — so a run of intermittent 404s doesn't accumulate forever.
          setJobs(prev => prev.map(job => (
            job.jobId === jobId && (job._404Count || 0) > 0
              ? { ...job, _404Count: 0, _404FirstAt: null }
              : job
          )));
          console.warn(`Poll ${jobId} transient error:`, error?.message || error);
        }
      }
      if (terminal) { stop(); return; }
      schedule(nextDelayMs());
    };

    // First run kicks off immediately.
    schedule(0);
  }, []);

  // Poll immediately when the tab regains focus - so a user coming
  // back to the app sees the freshest state without waiting for the
  // current backoff to elapse.
  useEffect(() => {
    const onVisibility = () => {
      if (typeof document === 'undefined' || document.hidden) return;
      // Reschedule any active pollers to run immediately.
      const active = Array.from(pollIntervalsRef.current.keys());
      active.forEach((jobId) => {
        const handle = pollIntervalsRef.current.get(jobId);
        if (handle) clearTimeout(handle);
        pollIntervalsRef.current.delete(jobId);
        startPolling(jobId);
      });
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility);
      return () => document.removeEventListener('visibilitychange', onVisibility);
    }
    return undefined;
  }, [startPolling]);

  // Show completion notification (works on any page)
  // One completion toast per job, ever. The poll path (inside a state updater that React may run twice), the
  // WebSocket path and a re-hydrated duplicate entry used to each fire their own toast for the same upload.
  const notifiedCompletionRef = useRef(new Set());
  const showCompletionNotification = useCallback((job) => {
    if (job?.jobId) {
      if (notifiedCompletionRef.current.has(job.jobId)) return;
      notifiedCompletionRef.current.add(job.jobId);
    }
    const { filename, totalTx, highConfidence, reviewNeeded } = job;
    // iter1254 · fallback: never show "0 transactions" when sub-counts
    // or the monotonic posted counter prove rows landed.
    const shownTotal = totalTx || (highConfidence + reviewNeeded) || job.feedPostedCount || 0;
    
    toast.success(
      <div className="cursor-pointer" onClick={() => navigate('/bank-feeds')}>
        <strong>Bank Statement Processed!</strong>
        <div className="text-sm mt-1">
          &quot;{filename}&quot; - {shownTotal} transactions
        </div>
        <div className="text-xs mt-1 text-green-200">
          {highConfidence} auto-posted, {reviewNeeded} need review
        </div>
        {job.learnedLayout && (
          <div className="text-xs mt-1 text-green-100" data-testid="learning-receipt">
            New bank layout learned — we taught ourselves to read this statement format, so next time it&apos;s instant.
          </div>
        )}
        <div className="text-xs mt-2 underline">Click to view →</div>
      </div>,
      { 
        duration: 15000,
        important: true,
      }
    );
  }, [navigate]);

  // Add a new background job
  const addJob = useCallback((jobData) => {
    // iter1242 · Preserve seed status/billing when the caller
    // already knows them (e.g. MyBanksPage "Review & confirm" pill
    // hydrates from `/upload-jobs/pending-payment`). Previously we
    // hard-coded `status: 'queued'` and dropped `billing`, which meant
    // the backlog-fee modal auto-opener saw a `queued` job with no
    // billing and skipped it until the next poll landed a few hundred
    // ms later. That race caused the "Review & confirm does nothing"
    // report on connected-account tiles that were already deferred.
    const newJob = {
      jobId: jobData.jobId,
      filename: jobData.filename,
      startedAt: new Date().toISOString(),
      status: jobData.initialStatus || jobData.status || 'queued',
      progress: jobData.progress || 0,
      totalTx: 0,
      processedTx: 0,
      highConfidence: 0,
      reviewNeeded: 0,
      notifiedBackground: false,
      billing: jobData.initialBilling || jobData.billing || null,
      bridgePreview: jobData.initialBridgePreview || jobData.bridgePreview || null,
    };

    setJobs(prev => [...prev, newJob]);

    // After 30s, gently reassure the user that the upload is safe to
    // leave alone. Uses toast.info (not toast.warning) - see iter1237:
    // there is no client-side timeout any more, so this is a
    // reassurance, not a "something might be wrong" nudge.
    setTimeout(() => {
      setJobs(prev => prev.map(job => {
        if (job.jobId !== jobData.jobId) return job;
        if (job.status === 'completed' || job.status === 'failed' || job.status === 'rejected') return job;
        if (job.notifiedBackground) return job;

        toast.info(
          <div>
            <strong>Processing in the background</strong>
            <div className="text-sm mt-1">
              &quot;{job.filename}&quot; - large statements can take several minutes.
            </div>
            <div className="text-xs mt-1">
              You can keep working. We&apos;ll notify you when it&apos;s done.
            </div>
          </div>,
          { duration: 8000 }
        );

        return { ...job, notifiedBackground: true };
      }));
    }, BACKGROUND_THRESHOLD_MS);

    // Start polling for this job
    startPolling(jobData.jobId);

    return newJob;
  }, [startPolling]);

  // Remove a job from tracking
  const removeJob = useCallback((jobId) => {
    // Stop polling
    const handle = pollIntervalsRef.current.get(jobId);
    if (handle) {
      clearTimeout(handle);
      pollIntervalsRef.current.delete(jobId);
    }
    
    setJobs(prev => prev.filter(job => job.jobId !== jobId));
  }, []);

  // Get active (non-terminal) jobs. `timeout` is intentionally NOT a
  // valid client-side status any more - see iter1237 comment above.
  const getActiveJobs = useCallback(() => {
    return jobs.filter(job => job.status !== 'completed' && job.status !== 'failed' && job.status !== 'cancelled' && job.status !== 'rejected');
  }, [jobs]);

  // Resume polling for any active jobs (called on app load)
  useEffect(() => {
    if (!isInitialized) return;

    const activeJobs = jobs.filter(job =>
      job.status !== 'completed' && job.status !== 'failed' && job.status !== 'cancelled' && job.status !== 'rejected'
    );

    activeJobs.forEach(job => {
      if (!pollIntervalsRef.current.has(job.jobId)) {
        startPolling(job.jobId, job.startedAt);
      }
    });
  }, [isInitialized, jobs, startPolling]);

  // Handle WebSocket events for bank feed completion
  const handleWebSocketEvent = useCallback((event) => {
    if (event.type === 'bank_feed_processed' && event.data?.status === 'completed') {
      const { job_id, filename, total_transactions, high_confidence, review_needed } = event.data;
      
      // Update job in state
      setJobs(prev => prev.map(job => {
        if (job.jobId !== job_id) return job;
        
        const updated = {
          ...job,
          status: 'completed',
          completedAt: new Date().toISOString(),
          totalTx: total_transactions,
          highConfidence: high_confidence,
          reviewNeeded: review_needed,
        };
        
        // Stop polling
        const handle = pollIntervalsRef.current.get(job_id);
        if (handle) {
          clearTimeout(handle);
          pollIntervalsRef.current.delete(job_id);
        }
        
        // Show notification
        showCompletionNotification(updated);
        // iter1443 R5 — cross-page refresh signal (WebSocket path).
        try {
          window.dispatchEvent(new CustomEvent('akf:bank-feed-refresh'));
        } catch (_e) { /* SSR / test envs */ }
        
        return updated;
      }));
    }
  }, [showCompletionNotification]);

  // iter1238 Phase 8.9 — explicit request to open the backlog-fee
  // review modal for a specific job. Only invoked from user-initiated
  // clicks (e.g. MyBanksPage "Review & confirm" pill). The provider
  // listens for `openRequested=true` and opens the modal for that job
  // only — no more page-load ambush.
  const requestReviewOpen = useCallback((jobId) => {
    setJobs(prev => prev.map(job => (
      job.jobId === jobId ? { ...job, openRequested: true } : job
    )));
  }, []);

  const clearReviewRequest = useCallback((jobId) => {
    setJobs(prev => prev.map(job => (
      job.jobId === jobId ? { ...job, openRequested: false } : job
    )));
  }, []);

  const value = {
    jobs,
    addJob,
    removeJob,
    getActiveJobs,
    handleWebSocketEvent,
    requestReviewOpen,
    clearReviewRequest,
    activeCount: getActiveJobs().length,
  };

  return (
    <BackgroundJobsContext.Provider value={value}>
      {children}
    </BackgroundJobsContext.Provider>
  );
};

export default BackgroundJobsContext;
